import React, { useState, useEffect } from 'react';
import { Mail, ArrowRight, AlertCircle, RefreshCw } from 'lucide-react';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import Section from '../forms/section/Section';

const OTPVerificationComponent = ({ onVerificationComplete }) => {
    const [email, setEmail] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [step, setStep] = useState('email'); // 'email' ou 'otp'
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);

    // Gestion du compte à rebours pour réenvoyer le code
    useEffect(() => {
        if (countdown <= 0) return;

        const timer = setTimeout(() => {
            setCountdown(countdown - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [countdown]);

    const formatCountdown = () => {
        const minutes = Math.floor(countdown / 60);
        const seconds = countdown % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const handleSendOTP = async (e) => {
        if (e) e.preventDefault();

        if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('Veuillez entrer une adresse email valide');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await authService.sendOTP(email);

            if (response.success) {
                toast.success('Un code de vérification a été envoyé à votre adresse email');
                setStep('otp');
                setCountdown(120); // 2 minutes avant de pouvoir renvoyer un code
            } else {
                throw new Error(response.message || 'Erreur lors de l\'envoi du code');
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi du code OTP:', error);
            setError(error.message || 'Impossible d\'envoyer le code de vérification');
            toast.error(error.message || 'Erreur lors de l\'envoi du code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();

        if (!otpCode || otpCode.length < 6) {
            setError('Veuillez entrer le code à 6 chiffres complet');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await authService.verifyOTP(email, otpCode);

            if (response.success) {
                toast.success('Email vérifié avec succès');

                // Si un callback de complétion est fourni, l'appeler
                if (onVerificationComplete) {
                    onVerificationComplete(email);
                }
            } else {
                throw new Error(response.message || 'Code de vérification incorrect');
            }
        } catch (error) {
            console.error('Erreur lors de la vérification du code OTP:', error);
            setError(error.message || 'Code de vérification incorrect');
            toast.error(error.message || 'Erreur lors de la vérification');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Section title="Vérification de votre adresse email">
            <div className="col-md-8 offset-2 p-4 bg-white rounded">
                <div className="text-center mb-4">
                    <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6'
                        }}
                    >
                        <Mail size={40} style={{ color: '#0A6EBD' }} />
                    </div>
                    <h4 className="mb-3">{step === 'email'
                        ? 'Vérifiez votre adresse email'
                        : 'Entrez le code de vérification'}</h4>
                    <p className="text-muted">
                        {step === 'email'
                            ? 'Pour sécuriser votre réclamation, nous avons besoin de vérifier votre adresse email'
                            : `Nous avons envoyé un code à 6 chiffres à ${email}`}
                    </p>
                </div>

                {error && (
                    <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                        <AlertCircle size={18} className="me-2" />
                        <div>{error}</div>
                    </div>
                )}

                {step === 'email' ? (
                    <form onSubmit={handleSendOTP}>
                        <div className="form-group mb-4">
                            <label htmlFor="email" className="form-label">Adresse Email <span className="text-danger">*</span></label>
                            <input
                                type="email"
                                className={`form-control ${error ? 'is-invalid' : ''}`}
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="nom@exemple.com"
                                required
                                autoFocus
                            />
                            <small className="form-text text-muted">
                                Nous vous enverrons un code de vérification à cette adresse
                            </small>
                        </div>
                        <div className="d-grid gap-2">
                            <button
                                type="submit"
                                className="brand-button d-flex justify-content-center align-items-center"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Chargement...</span>
                                    </div>
                                ) : null}
                                Recevoir le code
                                <ArrowRight size={18} className="ms-2" />
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOTP}>
                        <div className="form-group mb-4">
                            <label htmlFor="otpCode" className="form-label">Code de vérification <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control form-control-lg text-center ${error ? 'is-invalid' : ''}`}
                                id="otpCode"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="______"
                                maxLength={6}
                                autoComplete="one-time-code"
                                pattern="[0-9]{6}"
                                required
                                autoFocus
                            />
                            <small className="form-text text-muted">
                                Le code expire dans 10 minutes
                            </small>
                        </div>
                        <div className="d-grid gap-2 mb-3">
                            <button
                                type="submit"
                                className="brand-button d-flex justify-content-center align-items-center"
                                disabled={isLoading || otpCode.length < 6}
                            >
                                {isLoading ? (
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Chargement...</span>
                                    </div>
                                ) : null}
                                Vérifier le code
                                <ArrowRight size={18} className="ms-2" />
                            </button>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <button
                                type="button"
                                className="btn btn-link text-decoration-none"
                                onClick={() => setStep('email')}
                                disabled={isLoading}
                                style={{ color: '#0A6EBD' }}
                            >
                                Modifier l'email
                            </button>

                            {countdown > 0 ? (
                                <div className="text-muted small">
                                    Renvoyer le code dans {formatCountdown()}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none d-flex align-items-center"
                                    onClick={handleSendOTP}
                                    disabled={isLoading}
                                    style={{ color: '#0A6EBD' }}
                                >
                                    <RefreshCw size={16} className="me-1" />
                                    Renvoyer le code
                                </button>
                            )}
                        </div>
                    </form>
                )}
            </div>
            <style>{`
        .brand-button {
          background-color: #0A6EBD;
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 4px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .brand-button:hover {
          background-color: #085494;
        }

        .brand-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }

        .invalid-feedback {
          display: block !important;
          color: #dc3545;
          margin-top: 0.25rem;
        }

        .is-invalid {
          border-color: #dc3545 !important;
        }
      `}</style>
        </Section>
    );
};

export default OTPVerificationComponent;